import React, { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import usePageScrollLock from "../hooks/usePageScrollLock";

const ModalOverlay = styled(motion.div)`
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(0, 0, 0, 0.82); backdrop-filter: blur(8px);
  display: flex; align-items: center; justify-content: center; padding: 12px;
  overflow: hidden; overscroll-behavior: contain;
`;

const PickerCard = styled(motion.div)`
  background: #0f172a; border: 1px solid #1e293b; border-radius: 20px;
  width: min(380px, 100%); max-height: calc(100dvh - 24px);
  overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain;
  box-shadow: 0 50px 100px -20px rgba(0,0,0,0.8);
  position: relative;
  &:before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, #3b82f680, transparent);
  }
`;

const Header = styled.div`
  padding: 16px 12px; border-bottom: 1px solid #1e293b40;
  display: flex; align-items: center; justify-content: space-between;
  h3 { margin: 0; font-size: 0.85rem; font-weight: 900; color: #f8fafc; text-transform: uppercase; letter-spacing: 2px; }
  .nav { display: flex; gap: 8px; }
  .btn { 
    background: #1e293b; border: none; width: 44px; height: 44px; border-radius: 6px;
    color: #94a3b8; cursor: pointer; display: flex; align-items: center; justify-content: center;
    &:hover { background: #334155; color: #f8fafc; }
  }
`;

const Grid = styled.div`
  padding: 12px; display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px;
`;

const DayLabel = styled.div`
  font-size: 0.65rem; font-weight: 800; color: #475569; text-align: center;
  padding-bottom: 12px; text-transform: uppercase;
`;

const Day = styled.button`
  width: 100%; min-width: 0; height: 44px; padding: 0; border: 0; background: transparent;
  border-radius: 10px; display: flex; align-items: center; justify-content: center;
  font-family: inherit; font-size: 0.85rem; font-weight: 700; cursor: pointer; transition: all 0.2s;
  
  color: ${({ $isDisabled, $selected }) => 
    $selected ? '#ffffff' : '#f8fafc'};
    
  pointer-events: ${({ $isDisabled }) => $isDisabled ? 'none' : 'auto'};
  opacity: ${({ $isDisabled, $isCurrentMonth }) => {
    if ($isDisabled) return 0.2;
    if (!$isCurrentMonth) return 0.5; // Slightly dimmer but still very visible
    return 1;
  }};
  cursor: ${({ $isDisabled }) => $isDisabled ? 'not-allowed' : 'pointer'};

  ${({ $selected }) => $selected && `
    background: #3b82f6; color: white !important; 
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
    opacity: 1 !important;
  `}
  
  ${({ $isToday, $selected }) => $isToday && !$selected && `
    border: 1px solid #3b82f680; color: #3b82f6 !important; 
  `}

  &:hover {
    background: ${({ $selected, $isDisabled }) => $isDisabled ? 'transparent' : $selected ? '#3b82f6' : '#1e293b'};
    color: ${({ $isDisabled }) => $isDisabled ? 'inherit' : '#f8fafc'};
  }
  &:focus-visible { outline: 2px solid #00d1ff; outline-offset: -2px; }
`;

const Footer = styled.div`
  padding: 16px 20px 24px; border-top: 1px solid #1e293b40;
  display: flex; gap: 12px;
  .today-btn { 
    min-height: 44px;
    flex: 1; background: #1e293b; border: 1px solid #334155; color: #94a3b8;
    padding: 10px; border-radius: 10px; font-size: 0.75rem; font-weight: 900;
    cursor: pointer; text-transform: uppercase; letter-spacing: 1px;
    &:hover { background: #334155; color: #f8fafc; }
  }
  .confirm-btn {
    min-height: 44px;
    flex: 1; background: #3b82f6; border: none; color: white;
    padding: 10px; border-radius: 10px; font-size: 0.75rem; font-weight: 900;
    cursor: pointer; text-transform: uppercase; letter-spacing: 1px;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    &:hover { background: #2563eb; transform: translateY(-1px); }
  }
`;

export default function TacticalDatePicker({ isOpen, onClose, selectedDate, onSelect }) {
    usePageScrollLock(isOpen);
    const dialogRef = useRef(null);
    const titleId = useId();
    const defaultDate = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date();
    const [viewDate, setViewDate] = useState(new Date(defaultDate.getFullYear(), defaultDate.getMonth(), 1));

    useEffect(() => {
        if (!isOpen) return;
        const date = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date();
        setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    }, [isOpen, selectedDate]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const trigger = document.activeElement;
        dialogRef.current?.querySelector('button:not(:disabled)')?.focus();
        return () => trigger?.focus?.();
    }, [isOpen]);

    const handleDialogKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
        } else if (event.key === 'Tab') {
            const buttons = [...dialogRef.current.querySelectorAll('button:not(:disabled)')];
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        }
    };

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const daysArr = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevDaysInMonth = new Date(year, month, 0).getDate();

    const grid = [];
    // Prev padding
    for (let i = firstDay - 1; i >= 0; i--) {
        grid.push({ day: prevDaysInMonth - i, current: false, date: new Date(year, month - 1, prevDaysInMonth - i) });
    }
    // Current
    for (let i = 1; i <= daysInMonth; i++) {
        grid.push({ day: i, current: true, date: new Date(year, month, i) });
    }
    // Next padding
    const remaining = 42 - grid.length;
    for (let i = 1; i <= remaining; i++) {
        grid.push({ day: i, current: false, date: new Date(year, month + 1, i) });
    }

    const changeMonth = (offset) => {
        setViewDate(new Date(year, month + offset, 1));
    };

    const isSelected = (date) => {
        if (!selectedDate) return false;
        const d = new Date(`${selectedDate}T00:00:00`);
        return date.getFullYear() === d.getFullYear() && date.getMonth() === d.getMonth() && date.getDate() === d.getDate();
    };

    const isToday = (date) => {
        return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
    };

    const isPast = (date) => {
        const d = new Date(date);
        d.setHours(0,0,0,0);
        return d.getTime() < today.getTime();
    };

    const handleDateClick = (date) => {
        if (isPast(date)) return;
        // Format as YYYY-MM-DD in local time
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        onSelect(`${y}-${m}-${d}`);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <ModalOverlay
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <PickerCard
                        ref={dialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        onKeyDown={handleDialogKeyDown}
                        initial={{ opacity: 0, scale: 0.95, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: 10 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Header>
                            <h3 id={titleId}>{monthNames[month]} {year}</h3>
                            <div className="nav">
                                <button type="button" className="btn" aria-label="Previous month" onClick={() => changeMonth(-1)}><ChevronLeft size={16} /></button>
                                <button type="button" className="btn" aria-label="Next month" onClick={() => changeMonth(1)}><ChevronRight size={16} /></button>
                            </div>
                        </Header>

                        <Grid>
                            {daysArr.map(d => <DayLabel key={d}>{d}</DayLabel>)}
                            {grid.map((item, i) => {
                                const past = isPast(item.date);
                                const current = item.current;
                                const selected = isSelected(item.date);
                                
                                return (
                                    <Day
                                        type="button"
                                        key={i}
                                        disabled={past}
                                        aria-label={item.date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                        aria-pressed={selected}
                                        aria-current={isToday(item.date) ? 'date' : undefined}
                                        $isCurrentMonth={current}
                                        $selected={selected}
                                        $isToday={isToday(item.date)}
                                        $isDisabled={past}
                                        onClick={() => handleDateClick(item.date)}
                                    >
                                        {item.day}
                                    </Day>
                                );
                            })}
                        </Grid>

                        <Footer>
                            <button type="button" className="today-btn" onClick={() => handleDateClick(new Date())}>Today</button>
                            <button type="button" className="confirm-btn" onClick={onClose}>Done</button>
                        </Footer>
                    </PickerCard>
                </ModalOverlay>
            )}
        </AnimatePresence>
    );
}
